import { useEffect, useState, useCallback } from 'react';
import useFetchWithMsal from '../../../useFetchWithMsal';
import { thor_api_urls } from '../../common/Constants';
import { buildLoadingState, buildDataState, buildErrorState } from '../../../utils/StateUtils';
import { useThor } from '../context/ThorContext';
import StateHandler from '../../common/elements/StateHandler';
import Button from '../../common/elements/Button';
import SmallLoadingSpinner from '../../common/elements/SmallLoadingSpinner';
import { serverErrMsg } from '../utils/validators';

// ── Helpers ────────────────────────────────────────────────────────────────

function IntervalDisplay({ start, end }) {
    return <span>[ {start}, {end} ]</span>;
}

function computeOverlap(s1, e1, s2, e2) {
    const s = Math.max(s1, s2);
    const e = Math.min(e1, e2);
    return s <= e ? [s, e] : null;
}

function DualRangeSlider({ start, end, onStartChange, onEndChange }) {
    const min = 1, max = 100;
    const leftPct = ((start - min) / (max - min)) * 100;
    const rightPct = ((max - end) / (max - min)) * 100;
    return (
        <div className="field">
            <label className="label">Interval</label>
            <div className="control">
                <div className="dual-range-slider" style={{ marginBottom: 4 }}>
                    <div style={{
                        position: 'absolute', top: 7, left: 0, right: 0,
                        height: 6, background: '#dbdbdb', borderRadius: 3, pointerEvents: 'none',
                    }} />
                    <div style={{
                        position: 'absolute', top: 7,
                        left: `${leftPct}%`, right: `${rightPct}%`,
                        height: 6, background: '#48c78e', borderRadius: 3, pointerEvents: 'none',
                    }} />
                    <input
                        type="range" min={min} max={max} value={start}
                        style={{ zIndex: start >= end - 1 ? 5 : 3 }}
                        onChange={e => onStartChange(Math.min(Number(e.target.value), end - 1))}
                    />
                    <input
                        type="range" min={min} max={max} value={end}
                        style={{ zIndex: 4 }}
                        onChange={e => onEndChange(Math.max(Number(e.target.value), start + 1))}
                    />
                </div>
                <p className="help">{start}–{end}</p>
            </div>
        </div>
    );
}

// ── Add / Edit Group Modal ─────────────────────────────────────────────────

function GroupModal({ title, initialName = '', onClose, onSave }) {
    const [name, setName] = useState(initialName);
    const [saving, setSaving] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setSaving(true);
        onSave(name.trim()).finally(() => setSaving(false));
    };

    return (
        <div className="modal is-active">
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card">
                <header className="modal-card-head">
                    <p className="modal-card-title">{title}</p>
                    <button className="delete" aria-label="close" onClick={onClose} />
                </header>
                <form onSubmit={handleSubmit}>
                    <section className="modal-card-body">
                        <div className="field">
                            <label className="label">Name</label>
                            <div className="control">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="Exclusion group name..."
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required
                                    autoFocus
                                />
                            </div>
                        </div>
                    </section>
                    <footer className="modal-card-foot">
                        <Button type="submit" color="dark" disabled={!name.trim() || saving}>
                            {saving ? <SmallLoadingSpinner /> : (initialName ? 'Save' : 'Create')}
                        </Button>
                        <Button onClick={onClose}>Cancel</Button>
                    </footer>
                </form>
            </div>
        </div>
    );
}

// ── Add / Edit Experiment Modal ────────────────────────────────────────────

function ExperimentModal({ title, initialExperimentKey = null, initialStart = 1, initialEnd = 100, existingExperiments = [], onClose, onSave }) {
    const { execute } = useFetchWithMsal();
    const [eligibleExperiments, setEligibleExperiments] = useState([]);
    const [experimentKey, setExperimentKey] = useState(initialExperimentKey || '');
    const [start, setStart] = useState(Number(initialStart) || 1);
    const [end, setEnd] = useState(Number(initialEnd) || 100);
    const [saving, setSaving] = useState(false);
    const [loadingEligible, setLoadingEligible] = useState(!initialExperimentKey);

    const fetchEligible = useCallback(() => {
        if (initialExperimentKey) return;
        execute('GET', thor_api_urls.exclusionGroupEligibleExperiments)
            .then(data => setEligibleExperiments(Array.isArray(data) ? data : (data?.eligibleExperiments || [])))
            .catch(() => setEligibleExperiments([]))
            .finally(() => setLoadingEligible(false));
    }, [execute]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchEligible(); }, [fetchEligible]);

    const hasValidInterval = start >= 1 && end >= start && end <= 100;
    const utilization = hasValidInterval ? (end - start + 1) : '';

    const overlaps = hasValidInterval
        ? existingExperiments.flatMap(exp => {
            const ov = computeOverlap(start, end, exp.startAllocation, exp.endAllocation);
            return ov ? [{ key: exp.key, expStart: exp.startAllocation, expEnd: exp.endAllocation, ovStart: ov[0], ovEnd: ov[1] }] : [];
          })
        : [];

    const isValid = experimentKey && hasValidInterval;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!isValid) return;
        setSaving(true);
        onSave(experimentKey, Number(start), Number(end)).finally(() => setSaving(false));
    };

    return (
        <div className="modal is-active">
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card">
                <header className="modal-card-head">
                    <p className="modal-card-title">{title}</p>
                    <button className="delete" aria-label="close" onClick={onClose} />
                </header>
                <form onSubmit={handleSubmit}>
                    <section className="modal-card-body">
                        {!initialExperimentKey && (
                            <div className="field">
                                <label className="label">Experiment <span className="has-text-danger">*</span></label>
                                <div className="control">
                                    {loadingEligible ? <SmallLoadingSpinner /> : (
                                        <div className="select is-fullwidth">
                                            <select value={experimentKey} onChange={e => setExperimentKey(e.target.value)} required>
                                                <option value="">Select an experiment</option>
                                                {eligibleExperiments.map(exp => (
                                                    <option key={exp.key} value={exp.key}>{exp.name} ({exp.key})</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="field">
                            <label className="label">Interval start <span className="has-text-danger">*</span></label>
                            <div className="control">
                                <input
                                    className="input"
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={start}
                                    onChange={e => { const v = Number(e.target.value); if (v >= 1 && v <= 100) setStart(Math.min(v, end)); }}
                                    required
                                />
                            </div>
                            {end < start && <p className="help is-danger">Start must be ≤ end</p>}
                        </div>

                        <div className="field">
                            <label className="label">Interval end <span className="has-text-danger">*</span></label>
                            <div className="control">
                                <input
                                    className="input"
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={end}
                                    onChange={e => { const v = Number(e.target.value); if (v >= 1 && v <= 100) setEnd(Math.max(v, start)); }}
                                    required
                                />
                            </div>
                        </div>

                        <DualRangeSlider
                            start={start} end={end}
                            onStartChange={v => setStart(v)}
                            onEndChange={v => setEnd(v)}
                        />

                        <div className="field">
                            <label className="label">%</label>
                            <div className="control">
                                <input className="input" type="number" value={utilization} readOnly />
                            </div>
                        </div>

                        {overlaps.length > 0 && (
                            <div className="notification is-warning is-light p-3 mb-0">
                                <p className="has-text-weight-semibold">⚠ You have overlapping experiment slices</p>
                                <p className="is-size-7 mb-2">This could have undesired behaviors.</p>
                                <table className="table is-fullwidth is-narrow is-size-7">
                                    <thead>
                                        <tr>
                                            <th>Experiment Key</th>
                                            <th>Interval</th>
                                            <th>Overlap</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overlaps.map(o => (
                                            <tr key={o.key}>
                                                <td><code>{o.key}</code></td>
                                                <td>[{o.expStart}, {o.expEnd}]</td>
                                                <td className="has-text-danger has-text-weight-semibold">[{o.ovStart}, {o.ovEnd}]</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                    <footer className="modal-card-foot">
                        <Button type="submit" color="dark" disabled={!isValid || saving}>
                            {saving ? <SmallLoadingSpinner /> : (initialExperimentKey ? 'Save' : 'Add')}
                        </Button>
                        <Button onClick={onClose}>Cancel</Button>
                    </footer>
                </form>
            </div>
        </div>
    );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function ExclusionGroupsManager() {
    const { execute } = useFetchWithMsal();
    const { addToast } = useThor();

    const [groupsState, setGroupsState] = useState(buildLoadingState([]));
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [experimentsState, setExperimentsState] = useState(buildLoadingState([]));

    const [modal, setModal] = useState(null); // null | { type: 'addGroup'|'editGroup'|'addExp'|'editExp', data? }

    const loadGroups = useCallback(() => {
        setGroupsState(buildLoadingState([]));
        execute('GET', thor_api_urls.allExclusionGroups)
            .then(data => setGroupsState(buildDataState(data?.exclusionGroups || [])))
            .catch(err => setGroupsState(buildErrorState(err, [])));
    }, [execute]);

    const loadExperiments = useCallback((groupId) => {
        setExperimentsState(buildLoadingState([]));
        execute('GET', thor_api_urls.exclusionGroupExperiments(groupId))
            .then(data => setExperimentsState(buildDataState(data?.experimentExclusionGroups || [])))
            .catch(err => setExperimentsState(buildErrorState(err, [])));
    }, [execute]);

    useEffect(() => { loadGroups(); }, [loadGroups]);

    useEffect(() => {
        if (selectedGroup) loadExperiments(selectedGroup.exclusionGroupId);
        else setExperimentsState(buildLoadingState([]));
    }, [selectedGroup?.exclusionGroupId, loadExperiments]); // eslint-disable-line react-hooks/exhaustive-deps

    const closeModal = () => setModal(null);

    // ── Group CRUD ────────────────────────────────────────────────────────

    const handleCreateGroup = (name) =>
        execute('POST', thor_api_urls.allExclusionGroups, { name })
            .then(() => { addToast('Exclusion group created', 'success'); closeModal(); loadGroups(); })
            .catch(err => { addToast(serverErrMsg(err, 'Failed to create group'), 'danger'); throw err; });

    const handleEditGroup = (name) =>
        execute('PUT', thor_api_urls.exclusionGroup(modal.data.exclusionGroupId), { name })
            .then(() => {
                addToast('Exclusion group updated', 'success');
                closeModal();
                loadGroups();
                if (selectedGroup?.exclusionGroupId === modal.data.exclusionGroupId)
                    setSelectedGroup(prev => ({ ...prev, name }));
            })
            .catch(err => { addToast(serverErrMsg(err, 'Failed to update group'), 'danger'); throw err; });

    const handleDeleteGroup = (group) => {
        if (!window.confirm(`Delete exclusion group "${group.name}"?`)) return;
        execute('DELETE', thor_api_urls.exclusionGroup(group.exclusionGroupId))
            .then(() => {
                addToast('Exclusion group deleted', 'success');
                if (selectedGroup?.exclusionGroupId === group.exclusionGroupId) setSelectedGroup(null);
                loadGroups();
            })
            .catch(err => addToast(serverErrMsg(err, 'Failed to delete group'), 'danger'));
    };

    // ── Experiment CRUD ───────────────────────────────────────────────────

    const handleAddExperiment = (experimentKey, startExperiment, endExperiment) =>
        execute('POST', thor_api_urls.experimentInExclusionGroup(experimentKey, selectedGroup.exclusionGroupId), { startExperiment, endExperiment })
            .then(() => { addToast('Experiment added', 'success'); closeModal(); loadExperiments(selectedGroup.exclusionGroupId); loadGroups(); })
            .catch(err => { addToast(serverErrMsg(err, 'Failed to add experiment'), 'danger'); throw err; });

    const handleEditExperiment = (experimentKey, startExperiment, endExperiment) =>
        execute('PUT', thor_api_urls.experimentInExclusionGroup(experimentKey, selectedGroup.exclusionGroupId), { startExperiment, endExperiment })
            .then(() => { addToast('Experiment updated', 'success'); closeModal(); loadExperiments(selectedGroup.exclusionGroupId); })
            .catch(err => { addToast(serverErrMsg(err, 'Failed to update experiment'), 'danger'); throw err; });

    const handleDeleteExperiment = (exp) => {
        if (!window.confirm(`Remove "${exp.experimentName}" from this exclusion group?`)) return;
        execute('DELETE', thor_api_urls.experimentInExclusionGroup(exp.key, selectedGroup.exclusionGroupId))
            .then(() => { addToast('Experiment removed', 'success'); loadExperiments(selectedGroup.exclusionGroupId); loadGroups(); })
            .catch(err => addToast(serverErrMsg(err, 'Failed to remove experiment'), 'danger'));
    };

    const groups = groupsState.data || [];
    const experiments = experimentsState.data || [];

    return (
        <>
            <div className="columns">
                {/* Left panel — Exclusion Groups */}
                <div className="column">
                    <p className="subtitle is-5 mb-2">Exclusion Groups</p>
                    <StateHandler showLoader={groupsState.loading} showError={groupsState.error}>
                        <table className="table is-fullwidth is-bordered is-hoverable">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Experiments</th>
                                    <th>Utilization %</th>
                                    <th>Operations</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.length === 0 && (
                                    <tr><td colSpan={4} className="has-text-grey has-text-centered">No exclusion groups</td></tr>
                                )}
                                {groups.map(group => (
                                    <tr
                                        key={group.exclusionGroupId}
                                        className={selectedGroup?.exclusionGroupId === group.exclusionGroupId ? 'is-selected' : ''}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setSelectedGroup(group)}
                                    >
                                        <td>{group.name}</td>
                                        <td>{group.experiments}</td>
                                        <td>{group.utilization}</td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <div className="buttons are-small">
                                                <Button
                                                    size="small"
                                                    onClick={() => setModal({ type: 'editGroup', data: group })}
                                                >Edit</Button>
                                                <Button
                                                    size="small"
                                                    color="danger"
                                                    disabled={group.anyProdRunning || group.experiments > 0}
                                                    onClick={() => handleDeleteGroup(group)}
                                                >Delete</Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </StateHandler>
                    <Button
                        color="dark"
                        onClick={() => setModal({ type: 'addGroup' })}
                    >Add exclusion group</Button>
                </div>

                {/* Right panel — Experiments in selected group */}
                <div className="column">
                    <p className="subtitle is-5 mb-2">
                        {selectedGroup ? `Experiments — ${selectedGroup.name}` : 'Experiments'}
                    </p>
                    {!selectedGroup ? (
                        <p className="has-text-grey">Select an exclusion group to view its experiments.</p>
                    ) : (
                        <>
                            <StateHandler showLoader={experimentsState.loading} showError={experimentsState.error}>
                                <table className="table is-fullwidth is-bordered is-hoverable">
                                    <thead>
                                        <tr>
                                            <th>Experiment Name</th>
                                            <th>%</th>
                                            <th>Interval</th>
                                            <th>Operations</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {experiments.length === 0 && (
                                            <tr><td colSpan={4} className="has-text-grey has-text-centered">No experiments in this group</td></tr>
                                        )}
                                        {experiments.map(exp => (
                                            <tr key={exp.key}>
                                                <td>{exp.experimentName}</td>
                                                <td>{exp.endAllocation - exp.startAllocation + 1}</td>
                                                <td><IntervalDisplay start={exp.startAllocation} end={exp.endAllocation} /></td>
                                                <td>
                                                    <div className="buttons are-small">
                                                        <Button
                                                            size="small"
                                                            disabled={exp.isProdRunning}
                                                            onClick={() => setModal({ type: 'editExp', data: exp })}
                                                        >Edit</Button>
                                                        <Button
                                                            size="small"
                                                            color="danger"
                                                            disabled={exp.isProdRunning}
                                                            onClick={() => handleDeleteExperiment(exp)}
                                                        >Delete</Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </StateHandler>
                            <Button
                                color="dark"
                                onClick={() => setModal({ type: 'addExp' })}
                            >Add experiment</Button>
                        </>
                    )}
                </div>
            </div>

            {/* Modals */}
            {modal?.type === 'addGroup' && (
                <GroupModal title="Add exclusion group" onClose={closeModal} onSave={handleCreateGroup} />
            )}
            {modal?.type === 'editGroup' && (
                <GroupModal
                    title="Edit exclusion group"
                    initialName={modal.data.name}
                    onClose={closeModal}
                    onSave={handleEditGroup}
                />
            )}
            {modal?.type === 'addExp' && (
                <ExperimentModal
                    title="Add experiment"
                    existingExperiments={experiments}
                    onClose={closeModal}
                    onSave={handleAddExperiment}
                />
            )}
            {modal?.type === 'editExp' && (
                <ExperimentModal
                    title="Edit experiment"
                    initialExperimentKey={modal.data.key}
                    initialStart={modal.data.startAllocation}
                    initialEnd={modal.data.endAllocation}
                    existingExperiments={experiments.filter(e => e.key !== modal.data.key)}
                    onClose={closeModal}
                    onSave={handleEditExperiment}
                />
            )}
        </>
    );
}
